import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Select from '../ui/Select';
import Input from '../ui/Input';
import Button from '../ui/Button';

/**
 * EventFilters Component
 * Provides comprehensive filtering UI for events
 * - Text Search (title)
 * - City Dropdown
 * - Difficulty Select
 * - Event Type Select
 * - Date Range
 * - Max Price Slider
 */
const EventFilters = ({ filters, onFilterChange, onClearFilters, isOpen, onToggle }) => {
  const [localFilters, setLocalFilters] = useState({
    search: '',
    city: 'all',
    difficulty: 'all',
    eventType: 'all',
    dateFrom: '',
    dateTo: '',
    maxPrice: 50000,
    ...filters
  });

  // Sync localFilters when parent filters change (e.g., after clear)
  useEffect(() => {
    setLocalFilters({
      search: filters?.search || '',
      city: filters?.city || 'all',
      difficulty: filters?.difficulty || 'all',
      eventType: filters?.eventType || 'all',
      dateFrom: filters?.dateFrom || '',
      dateTo: filters?.dateTo || '',
      maxPrice: filters?.maxPrice ?? 50000,
    });
  }, [filters]);

  const cityOptions = [
    { value: 'all', label: 'Tüm Şehirler' },
    { value: 'istanbul', label: 'İstanbul' },
    { value: 'izmir', label: 'İzmir' },
    { value: 'antalya', label: 'Antalya' },
    { value: 'mugla', label: 'Muğla' },
    { value: 'aydin', label: 'Aydın' },
    { value: 'canakkale', label: 'Çanakkale' },
    { value: 'bursa', label: 'Bursa' },
    { value: 'bodrum', label: 'Bodrum' },
    { value: 'fethiye', label: 'Fethiye' },
    { value: 'marmaris', label: 'Marmaris' },
  ];

  const difficultyOptions = [
    { value: 'all', label: 'Tüm Seviyeler' },
    { value: 'Beginner', label: 'Başlangıç' },
    { value: 'Intermediate', label: 'Orta' },
    { value: 'Advanced', label: 'İleri' },
    { value: 'Pro', label: 'Uzman' },
  ];

  const eventTypeOptions = [
    { value: 'all', label: 'Tüm Etkinlikler' },
    { value: 'tour', label: 'Tur' },
    { value: 'training', label: 'Eğitim' },
    { value: 'competition', label: 'Yarışma' },
    { value: 'social', label: 'Sosyal' },
  ];

  const handleApplyFilters = () => {
    onFilterChange(localFilters);
    if (window?.innerWidth < 768) {
      onToggle?.();
    }
  };

  const handleClear = () => {
    const clearedFilters = {
      search: '',
      city: 'all',
      difficulty: 'all',
      eventType: 'all',
      dateFrom: '',
      dateTo: '',
      maxPrice: 50000
    };
    setLocalFilters(clearedFilters);
    onClearFilters?.();
  };

  const handleMaxPriceChange = (e) => {
    setLocalFilters({ ...localFilters, maxPrice: parseFloat(e?.target?.value) || 50000 });
  };

  return (
    <>
      {/* Mobile Filter Toggle */}
      <div className="lg:hidden mb-4">
        <Button
          variant="outline"
          fullWidth
          iconName="SlidersHorizontal"
          iconPosition="left"
          onClick={onToggle}
        >
          Filtreler
        </Button>
      </div>

      {/* Filter Panel */}
      <div
        className={`
          fixed lg:relative inset-0 lg:inset-auto z-50 lg:z-auto
          bg-background lg:bg-transparent
          transition-transform duration-300 lg:transition-none
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        <div className="h-full lg:h-auto overflow-y-auto lg:overflow-visible p-4 lg:p-0">
          {/* Mobile Header */}
          <div className="flex items-center justify-between mb-6 lg:hidden">
            <h2 className="text-xl font-semibold">Filtreler</h2>
            <button
              onClick={onToggle}
              className="p-2 hover:bg-muted rounded-lg transition-smooth"
              aria-label="Filtreleri kapat"
            >
              <Icon name="X" size={24} />
            </button>
          </div>

          {/* Filter Content */}
          <div className="bg-card rounded-xl p-4 md:p-6 shadow-organic space-y-6">
            {/* Text Search */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Etkinlik Ara
              </label>
              <Input
                type="search"
                placeholder="Başlık veya açıklama ara..."
                value={localFilters?.search || ''}
                onChange={(e) => setLocalFilters({ ...localFilters, search: e?.target?.value })}
                className="w-full"
              />
            </div>

            {/* City Dropdown */}
            <Select
              label="Şehir"
              options={cityOptions}
              value={localFilters?.city || 'all'}
              onChange={(value) => setLocalFilters({ ...localFilters, city: value })}
            />

            {/* Difficulty Select */}
            <Select
              label="Zorluk Seviyesi"
              options={difficultyOptions}
              value={localFilters?.difficulty || 'all'}
              onChange={(value) => setLocalFilters({ ...localFilters, difficulty: value })}
            />

            {/* Event Type Select */}
            <Select
              label="Etkinlik Tipi"
              options={eventTypeOptions}
              value={localFilters?.eventType || 'all'}
              onChange={(value) => setLocalFilters({ ...localFilters, eventType: value })}
            />

            {/* Date Range */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Tarih Aralığı
                </label>
                <Input
                  type="date"
                  label="Başlangıç"
                  value={localFilters?.dateFrom || ''}
                  onChange={(e) => setLocalFilters({ ...localFilters, dateFrom: e?.target?.value })}
                />
              </div>
              <Input
                type="date"
                label="Bitiş"
                value={localFilters?.dateTo || ''}
                onChange={(e) => setLocalFilters({ ...localFilters, dateTo: e?.target?.value })}
              />
            </div>

            {/* Max Price Slider */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Maksimum Fiyat: ₺{localFilters?.maxPrice ?? 50000}
              </label>
              <input
                type="range"
                min="0"
                max="50000"
                step="100"
                value={localFilters?.maxPrice ?? 50000}
                onChange={handleMaxPriceChange}
                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>₺0</span>
                <span>₺50000</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t border-border">
              <Button
                variant="outline"
                fullWidth
                onClick={handleClear}
              >
                Temizle
              </Button>
              <Button
                variant="default"
                fullWidth
                onClick={handleApplyFilters}
              >
                Uygula
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-foreground/20 z-40 lg:hidden"
          onClick={onToggle}
        />
      )}
    </>
  );
};

export default EventFilters;